import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ExternalLink, Bell } from 'lucide-react';
import useLiveNotifications from '../hooks/useLiveNotifications';
import BrandLogo from './BrandLogo';
import { Button } from './ui/button';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';

// Admin-managed announcements (see backend api/v1/notifications.py), shown
// full-screen on top of the app on every login. The backend decides what is
// live — only paying (Stripe) subscribers get anything back.
//
// Ids already displayed this session are kept in sessionStorage, so a page
// reload doesn't show them again; logout clears the list (AuthContext) so
// the next login shows every live notification again.
//
// The same notifications also stay reachable afterwards through a bell
// button (bottom-right, stacked above the support-chat launcher) for as long
// as they're live.
const SHOWN_IDS_KEY = 'shown_login_notifications';

const getShownIds = () => {
  try {
    return JSON.parse(sessionStorage.getItem(SHOWN_IDS_KEY)) || [];
  } catch {
    return [];
  }
};

const markShown = (id) => {
  const ids = getShownIds();
  if (!ids.includes(id)) {
    sessionStorage.setItem(SHOWN_IDS_KEY, JSON.stringify([...ids, id]));
  }
};

// Ids the user has already opened from the bell this session. While any live
// notification is missing from here the bell keeps ringing; like the list
// above, logout clears it (AuthContext).
const VIEWED_IDS_KEY = 'viewed_bell_notifications';

const getViewedIds = () => {
  try {
    return JSON.parse(sessionStorage.getItem(VIEWED_IDS_KEY)) || [];
  } catch {
    return [];
  }
};

const markViewed = (ids) => {
  try {
    sessionStorage.setItem(VIEWED_IDS_KEY, JSON.stringify([...new Set([...getViewedIds(), ...ids])]));
  } catch {
    // Storage unavailable — the bell just rings again after a reload.
  }
};

const LoginNotificationOverlay = () => {
  const { eligible, notifications } = useLiveNotifications();
  const [queue, setQueue] = useState([]);

  useEffect(() => {
    const shownIds = getShownIds();
    setQueue(notifications.filter((n) => !shownIds.includes(n.id)));
  }, [notifications]);

  const current = queue[0];

  useEffect(() => {
    if (current) markShown(current.id);
  }, [current]);

  const dismiss = useCallback(() => setQueue((q) => q.slice(1)), []);

  useEffect(() => {
    if (!current) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') dismiss(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [current, dismiss]);

  if (!eligible) return null;
  if (!current) return <NotificationBell notifications={notifications} />;

  // Portaled to <body> and above every other fixed layer (theme toggle,
  // chat widget, sync banners) so the app underneath is fully covered.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      data-testid="login-notification-overlay"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/90 backdrop-blur-md p-4"
    >
      <div className="relative w-full max-w-lg rounded-2xl border border-border bg-background p-8 shadow-2xl text-center">
        <button
          onClick={dismiss}
          data-testid="login-notification-close"
          aria-label="Cerrar"
          className="absolute top-3 right-3 rounded-full p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="w-5 h-5" />
        </button>

        <BrandLogo className="h-8 mx-auto mb-6" />
        <p className="text-base text-foreground whitespace-pre-line leading-relaxed">{current.message}</p>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          {current.link_url && (
            <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white">
              <a href={current.link_url} target="_blank" rel="noopener noreferrer" onClick={dismiss}>
                {current.link_label || 'Ingresa aquí'}
                <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </Button>
          )}
          <Button variant="outline" onClick={dismiss}>
            Cerrar
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Always-visible entry point to the live notifications, so a user who closed
// the login overlay can read them (and follow their links) again. One z-step
// below the support chat so its open panel covers the bell.
const NotificationBell = ({ notifications }) => {
  const [viewedIds, setViewedIds] = useState(getViewedIds);
  const hasUnviewed = notifications.some((n) => !viewedIds.includes(n.id));

  const onOpenChange = (open) => {
    if (!open || !hasUnviewed) return;
    const ids = notifications.map((n) => n.id);
    markViewed(ids);
    setViewedIds(getViewedIds());
  };

  return (
    <Popover onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="notification-bell"
          aria-label="Notificaciones"
          title="Notificaciones"
          className="fixed bottom-20 right-5 z-[9997] flex h-12 w-12 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-lg transition-colors hover:bg-muted hover:text-foreground"
        >
          <Bell className={`w-5 h-5 origin-top ${hasUnviewed ? 'animate-bell-ring text-blue-600 dark:text-blue-400' : ''}`} />
          {notifications.length > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-semibold text-white">
              {notifications.length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={8}
        data-testid="notification-panel"
        className="z-[9999] w-[360px] max-w-[calc(100vw-2.5rem)] p-0"
      >
        <div className="border-b border-border px-4 py-3 text-sm font-semibold text-foreground">
          Notificaciones
        </div>
        {notifications.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">No tienes notificaciones</p>
        ) : (
          <ul className="max-h-[60vh] divide-y divide-border overflow-y-auto">
            {notifications.map((n) => (
              <li key={n.id} className="px-4 py-3">
                <p className="text-sm text-foreground whitespace-pre-line leading-relaxed">{n.message}</p>
                {n.link_url && (
                  <a
                    href={n.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {n.link_label || 'Ingresa aquí'}
                    <ExternalLink className="w-3.5 h-3.5 ml-1" />
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default LoginNotificationOverlay;
